























import {
  computed, defineComponent, onUnmounted, reactive,
} from '@vue/composition-api';
import { Route } from 'vue-router';
import { useToast } from 'vue-toastification/composition';
import AppFooter from '../footer/AppFooter.vue';
import { subscribe } from '../../../shared/eventbus/eventbus';
import { Events } from '../../../shared/eventbus/events';
import { useRouter } from '../../composables/use-router';
import { PaymentStorageService } from '../../service/payment/paymentStorage';
import { useStore } from '../../store';
import { PaymentResponse, PaymentStates } from '../../store/payment/types';
import { useI18N } from '../../composables/use-vue-i18n';
import EditUnits from '../../component/modal/gamecloud/EditUnits.vue';
import { BaseToastTypeEnums, createToastTemplate } from '../../component/toast/BaseToastTemplateUse';
import SideNavigation from '../../component/navigation/side/SideNavigation.vue';

export default defineComponent({
  name: 'BaseLayout',
  components: {
    EditUnits,
    BaseRechargeModal: () => import(/* webpackChunkName: "vue.recharge" */'../../component/modal/recharge/BaseRechargeModal.vue'),
    AppFooter,
    SideNavigation,
  },
  setup: () => {
    const { route, router } = useRouter();
    const store = useStore();
    const i18n = useI18N();

    const state = reactive({
      rechargePreferableAmount: undefined,
      showRechargeModal: false,
      editModalShow: false,
      editModalOrderId: undefined,
      getPayment: computed((): PaymentResponse => store.getters['payment/getPayment']),
    });

    const isRechargePayment = (rechargePaymentRoute: Route): boolean => {
      if (!('pid' in rechargePaymentRoute.query)) {
        return false;
      }

      const service = PaymentStorageService.get();
      return service
        && service.equalMode('recharge')
        && service.equalId(rechargePaymentRoute.query.pid);
    };

    // --- we cannot use the route object coming from onReady
    // --- since this will only return valid routes (no * routes)
    router.onReady(async () => {
      const currentRoute = route.value;

      if (currentRoute && isRechargePayment(currentRoute)) {
        const service = PaymentStorageService.get();

        store.dispatch('payment/getPayment', { paymentId: service.getPayment().id });
      }

      if ('action' in currentRoute.query && currentRoute.query.action === 'showCredit') {
        state.rechargePreferableAmount = 'amount' in currentRoute.query && !Number.isNaN(currentRoute.query.amount)
          ? Number(currentRoute.query.amount)
          : undefined;

        state.showRechargeModal = true;
      }
    });

    const toast = useToast();

    const unsubscribe = store.subscribe((mutation) => {
      switch (mutation.type) {
        case 'payment/getPaymentLoaded':
          if (isRechargePayment(route.value)) {
            switch (state.getPayment.status) {
              case PaymentStates.SUCCESS:
                toast.success(createToastTemplate({
                  type: BaseToastTypeEnums.SUCCESS,
                  title: i18n.t('modal:recharge_account.feedback.payment_success.title'),
                  text: i18n.t('modal:recharge_account.feedback.payment_success.text'),
                }));
                break;

              case PaymentStates.PENDING:
                toast.info(createToastTemplate({
                  type: BaseToastTypeEnums.INFO,
                  title: i18n.t('modal:recharge_account.feedback.payment_pending.title'),
                  text: i18n.t('modal:recharge_account.feedback.payment_pending.text'),
                }));
                state.showRechargeModal = true;
                break;

              default:
                toast.error(createToastTemplate({
                  type: BaseToastTypeEnums.DANGER,
                  title: i18n.t('modal:recharge_account.feedback.payment_failed.title'),
                  text: i18n.t('modal:recharge_account.feedback.payment_failed.text'),
                }));
                state.showRechargeModal = true;
                break;
            }
          }
          break;
      }
    });

    const subscriptions = [];
    subscriptions.push(
      subscribe(Events.OpenRechargeModal, (payload) => {
        if (payload && 'amount' in payload) {
          state.rechargePreferableAmount = payload.amount;
        }

        state.showRechargeModal = true;
      }),
      subscribe(Events.OpenServerUnitEditModal, (payload) => {
        if (payload && 'orderId' in payload) {
          state.editModalOrderId = payload.orderId;
          state.editModalShow = true;
        }
      }),
    );

    onUnmounted(() => {
      unsubscribe();

      subscriptions.forEach((busUnsubscribe) => busUnsubscribe());
    });

    return state;
  },
});
